import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import type {CSSProperties} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Footnote} from "@khanacademy/wonder-blocks-typography";
import {useQuery} from "@khan/data-gql";
import {isActivitiesPage} from "@khan/guide-core-helpers";
import {mediaQueries} from "@khan/shared-styles";
import {useLocation} from "@khan/urls";

import {USER_FOR_CONVERSATION_NOTICE_QUERY} from "./user-for-conversation-notice-query.ts";

/* As designed */
const NOTICE_HEIGHT = 44;

interface Props {
    // Used to specify custom styling in the case for the Activity UI.
    style?: CSSProperties;
}

/**
 * A small box that displays below the chat input and notifies the user that
 * their conversation is recorded.
 */
export const ConversationRecordedNotice = ({
    style,
}: Props): React.ReactElement | null => {
    const [result] = useQuery(USER_FOR_CONVERSATION_NOTICE_QUERY);
    const {pathname} = useLocation();

    // TODO(mattmorgan) Handle the error case.
    if (result.status !== "success" || !result.data?.user) {
        return null;
    }

    const {
        user: {hasCoach, hasParents},
    } = result.data;

    let conversationNotice: React.ReactNode;

    if (!hasCoach && !hasParents) {
        conversationNotice = (
            <Trans>
                This chat is logged. You can view in your{" "}
                <Link href="/profile/me/khanmigo/history" target="_blank">
                    chat history
                </Link>
                .
            </Trans>
        );
    }

    if (hasCoach && hasParents) {
        conversationNotice = t`This chat is logged and viewable by your parent and teacher.`;
    } else if (hasParents) {
        conversationNotice = t`This chat is logged and viewable by your parent.`;
    } else if (hasCoach) {
        conversationNotice = t`This chat is logged and viewable by your teacher.`;
    }

    return (
        <View style={[styles.conversationNotice, style]} role="status">
            <Footnote style={styles.footnote}>
                {t`Do NOT share any personal data.`}
            </Footnote>
            <Strut
                size={spacing.xxxSmall_4}
                style={[
                    // Only add spacing when we display the footnotes side-by-side
                    // for the desktop activity UI.
                    styles.hidden,
                    isActivitiesPage(pathname) && styles.spacing,
                ]}
            />
            <Footnote style={styles.footnote}>{conversationNotice}</Footnote>
        </View>
    );
};

const styles = {
    conversationNotice: {
        minHeight: NOTICE_HEIGHT,
        padding: spacing.xxxSmall_4,
        justifyContent: "center",
        alignItems: "center",
        justifyItems: "center",
        color: semanticColor.core.foreground.neutral.subtle,
    },
    footnote: {
        textAlign: "center",
    },
    hidden: {
        display: "none",
        visibility: "hidden",
    },
    spacing: {
        // Only display spacing on the desktop version of activity UI.
        [mediaQueries.mdOrLarger]: {
            display: "unset",
            visibility: "visible",
        },
    },
} as const;
